import { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { TrendingUp, Award, Clock, Target, Zap, Calendar } from 'lucide-react';
import analyticsService from '../services/analyticsService';
import { format } from 'date-fns';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [progressData, setProgressData] = useState([]);
  const [quizPerformance, setQuizPerformance] = useState(null);
  const [timeAnalytics, setTimeAnalytics] = useState(null);
  const [badgeTimeline, setBadgeTimeline] = useState([]);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    fetchAllData();
  }, [timeRange]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, progressTimeData, quizData, timeData, badgeData] = await Promise.all([
        analyticsService.getOverview(),
        analyticsService.getProgressOverTime(timeRange),
        analyticsService.getQuizPerformance(),
        analyticsService.getTimeAnalytics(),
        analyticsService.getBadgeTimeline(),
      ]);

      setOverview(overviewData);
      setProgressData(progressTimeData);
      setQuizPerformance(quizData);
      setTimeAnalytics(timeData);
      setBadgeTimeline(badgeData);
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.message || error.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Analytics</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
            <div className="mt-4 text-sm text-gray-500">
              <p>Check the browser console (F12) for more details.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Prepare completion pie chart data
  const completionData = overview ? [
    { name: 'Modules', value: overview.breakdown.modules.completed, total: overview.breakdown.modules.total },
    { name: 'Labs', value: overview.breakdown.labs.completed, total: overview.breakdown.labs.total },
    { name: 'Capstones', value: overview.breakdown.capstones.completed, total: overview.breakdown.capstones.total },
  ] : [];

  // Prepare quiz score distribution data
  const scoreDistributionData = quizPerformance ? Object.entries(quizPerformance.score_distribution).map(([range, count]) => ({
    range,
    count,
  })) : [];

  // Prepare time by day data
  const timeByDayData = timeAnalytics ? Object.entries(timeAnalytics.by_day_of_week).map(([day, hours]) => ({
    day: day.substring(0, 3), // Mon, Tue, etc.
    hours,
  })) : [];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Learning Analytics</h1>
          <p className="text-gray-600">Track your progress, performance, and achievements</p>
        </div>

        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Completion Rate */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <Target className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{overview.completion_percentage}%</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Completion Rate</h3>
              <p className="text-xs text-gray-500 mt-1">
                {overview.completed_content} of {overview.total_content} items
              </p>
            </div>

            {/* Average Quiz Score */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <Award className="h-8 w-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{overview.avg_quiz_score}%</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Avg Quiz Score</h3>
              <p className="text-xs text-gray-500 mt-1">
                {quizPerformance?.total_attempts || 0} attempts
              </p>
            </div>

            {/* Total Time */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <Clock className="h-8 w-8 text-orange-600" />
                <span className="text-2xl font-bold text-orange-600">{overview.total_time_hours}h</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Time Invested</h3>
              <p className="text-xs text-gray-500 mt-1">
                {timeAnalytics?.total_sessions || 0} sessions
              </p>
            </div>

            {/* Learning Streak */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <Zap className="h-8 w-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">{overview.current_streak}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">Day Streak</h3>
              <p className="text-xs text-gray-500 mt-1">
                {overview.badges_earned} badges earned
              </p>
            </div>
          </div>
        )}

        {/* Progress Over Time Chart */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Progress Over Time</h2>
            <div className="flex gap-2">
              {[7, 14, 30, 60].map((days) => (
                <button
                  key={days}
                  onClick={() => setTimeRange(days)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    timeRange === days
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), 'MMM d')}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(date) => format(new Date(date), 'MMM d, yyyy')}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.3}
                name="Total Completed"
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.3}
                name="Daily Completions"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Completion Breakdown & Quiz Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Completion Breakdown Pie Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Completion by Content Type</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={completionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, total }) => `${name}: ${value}/${total}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {completionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {overview && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    Modules
                  </span>
                  <span className="font-semibold">{overview.breakdown.modules.percentage}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Labs
                  </span>
                  <span className="font-semibold">{overview.breakdown.labs.percentage}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                    Capstones
                  </span>
                  <span className="font-semibold">{overview.breakdown.capstones.percentage}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Quiz Score Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Quiz Score Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scoreDistributionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                />
                <Bar dataKey="count" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {quizPerformance && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{quizPerformance.avg_score}%</p>
                  <p className="text-xs text-gray-600">Average Score</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{quizPerformance.pass_rate}%</p>
                  <p className="text-xs text-gray-600">Pass Rate</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Time Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Time by Day of Week */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Study Time by Day</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeByDayData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                />
                <Bar dataKey="hours" fill="#10B981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {timeAnalytics && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  💡 You study most on <span className="font-semibold">
                    {Object.entries(timeAnalytics.by_day_of_week).sort(([,a], [,b]) => b - a)[0][0]}
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Time by Capstone */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Time by Capstone</h2>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {timeAnalytics?.by_capstone.slice(0, 10).map((capstone, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {capstone.capstone_title}
                    </p>
                    <p className="text-xs text-gray-500">{capstone.session_count} sessions</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(100, (capstone.total_hours / Math.max(...timeAnalytics.by_capstone.map(c => c.total_hours))) * 100)}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                      {capstone.total_hours}h
                    </span>
                  </div>
                </div>
              ))}
              {timeAnalytics?.by_capstone.length === 0 && (
                <p className="text-center text-gray-500 py-8">No capstone time tracked yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Badge Timeline */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6">
            <Award className="h-6 w-6 text-yellow-600" />
            <h2 className="text-xl font-bold text-gray-900">Achievement Timeline</h2>
          </div>
          {badgeTimeline.length > 0 ? (
            <div className="space-y-4">
              {badgeTimeline.map((badge, index) => (
                <div key={badge.id} className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={`text-4xl ${badge.color ? `text-${badge.color}-600` : 'text-yellow-600'}`}>
                    {badge.icon || '🏆'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{badge.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{badge.description}</p>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(badge.earned_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      #{badgeTimeline.length - index}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No badges earned yet</p>
              <p className="text-sm text-gray-400 mt-1">Keep learning to unlock achievements!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
